"use client";

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/components/providers/user-provider";
import { Footer } from "@/components/ui/footer-section";
import { useUserStore } from "@/store/userStore";
import { useRouter } from "@/i18n/navigation";
import HeroSection from "@/components/home/HeroSection";
import NavbarSection from "@/components/home/NavbarSection";
import { TestimonialSection } from "@/components/home/TestimonialSection";
import { FeatureSection } from "@/components/home/FeatureSection";
import { LanguagesSection } from "@/components/home/LanguagesSection";
import { PricingSection } from "@/components/home/PricingSection";
import { FAQSection } from "@/components/home/FAQSection";
import { CURRENCY_RATES } from "@/lib/utils/currency";

export default function HomePageClient() {
  // Add state for mobile detection
  const menuUploadRef = useRef<HTMLDivElement>(null);
  const { session, isLoading } = useAuth();
  const { subscription } = useUserStore();
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [isProcessingMenu, setIsProcessingMenu] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({
    fileUploaded: false,
    analyzing: false,
    extracting: false,
    creating: false,
  });

  // State for pricing section
  const [selectedCurrency, setSelectedCurrency] =
    useState<keyof typeof CURRENCY_RATES>("USD");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [creditAmount, setCreditAmount] = useState(10);

  const router = useRouter();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && session) {
      router.replace("/dashboard");
    }
  }, [session, isLoading, router]);

  // Function to handle section navigation and close menu
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 40; // Height of fixed header (56px) + some padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Add handler for upload complete - never show signup dialog for completed uploads
  const handleUploadComplete = (): void => {
    // No longer showing the signup prompt after uploads
    // We'll let the user see their completed menu right away
  };

  // Add handlers for processing state
  const handleProcessingStart = (progress: {
    fileUploaded: boolean;
    analyzing: boolean;
    extracting: boolean;
    creating: boolean;
  }) => {
    setIsProcessingMenu(true);
    setProcessingProgress(progress);
    setShowSignupPrompt(false); // Ensure signup prompt is hidden when processing
  };

  const handleProcessingComplete = () => {
    setIsProcessingMenu(false);
    setShowSignupPrompt(false); // Never show signup dialog after processing
    setProcessingProgress({
      fileUploaded: false,
      analyzing: false,
      extracting: false,
      creating: false,
    });
  };

  // Don't render the page content if loading or redirecting
  if (isLoading || session) {
    return null; // Or a loading indicator component
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Enhanced background gradient effects */}
      <div
        className="absolute top-0 left-0 right-0 h-[100vh] overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[128px] opacity-30 animate-pulse" />
        <div
          className="absolute top-[20%] right-[20%] w-[600px] h-[600px] bg-primary/20 blur-[128px] opacity-20 animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-[30%] left-[15%] w-[400px] h-[400px] bg-blue-500/10 blur-[128px] opacity-10 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      {/* Use the new NavbarSection component as header */}
      <header>
        <NavbarSection
          session={session}
          isLoading={isLoading}
          subscription={subscription}
          scrollToSection={scrollToSection}
        />
      </header>

      <main id="main-content">
        {/* Hero section (first section with h1) */}
        <HeroSection
          menuUploadRef={menuUploadRef}
          handleUploadComplete={handleUploadComplete}
          handleProcessingStart={handleProcessingStart}
          handleProcessingComplete={handleProcessingComplete}
          showSignupPrompt={showSignupPrompt}
          setShowSignupPrompt={setShowSignupPrompt}
          isProcessingMenu={isProcessingMenu}
          processingProgress={processingProgress}
        />

        {/* Features section */}
        <section aria-labelledby="features-heading">
          <FeatureSection />
        </section>

        {/* Languages section */}
        <section aria-labelledby="languages-heading">
          <LanguagesSection />
        </section>

        {/* Pricing section */}
        <section aria-labelledby="pricing-heading">
          <PricingSection
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            billingInterval={billingInterval}
            setBillingInterval={setBillingInterval}
            creditAmount={creditAmount}
            setCreditAmount={setCreditAmount}
          />
        </section>

        {/* FAQ section */}
        <section aria-labelledby="faq-heading">
          <FAQSection />
        </section>

        {/* Testimonials section */}
        <section aria-labelledby="testimonials-heading">
          <TestimonialSection />
        </section>
      </main>
      <footer>
        <Footer />
      </footer>
    </div>
  );
}
